import React, { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { get, upperFirst } from 'lodash';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSubscriptionQueries from '@dashboard/hooks/queries/useSubscriptionQueries';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import PageHeader from '@dashboard/components/PageHeader';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import Dash from '@dashboard/components/Dash';
import SubscriptionStatusBadge from '@dashboard/components/SubscriptionStatusBadge/SubscriptionStatusBadge';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { formatDateTime } from '@dashboard/lib/format-date';
import isRecoveredAbandonment from '@dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import { FILTER_OPTIONS } from '@dashboard/lib/subscriptionsConstants';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';
import { orderIdColumn } from '@dashboard/lib/orderId';
import PageTitle from '@dashboard/components/PageTitle';

export function getSubscriptionColumns({ options = {} } = {}) {
  const columns = [orderIdColumn];

  if (!options.hideCustomer) {
    columns.push({
      accessorKey: 'customerEmail',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Customer" />,
      cell: ({ row }) => {
        const subscription = row.original;
        return (
          <div className="max-w-xs">
            <div className="font-medium">
              {get(subscription, 'customer.email') || get(subscription, 'customerEmail') || (
                <Dash />
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-muted-foreground">
              {get(subscription, 'customer.name')}
            </div>
          </div>
        );
      },
      enableSorting: true,
    });
  }

  columns.push({
    accessorKey: 'amount',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => {
      const subscription = row.original;
      return (
        <span className="font-medium">
          {getPriceLabel({
            amount: subscription.amount,
            currency: subscription.currency,
            interval: subscription.interval,
            intervalCount: subscription.intervalCount,
            type: 'subscription',
          })}
        </span>
      );
    },
    enableSorting: false,
  });

  if (!options.hidePaymentLink) {
    columns.push({
      id: 'page',
      header: 'Product',
      cell: ({ row }) => (row.original.page && <PageTitle page={row.original.page} />) || <Dash />,
      enableSorting: false,
    });
  }

  columns.push({
    accessorKey: 'createdAt',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    enableSorting: true,
  });

  if (!options.hideAbandonment) {
    columns.push({
      id: 'abandonment',
      header: 'Abandonment',
      cell: ({ row }) => {
        const subscription = row.original;
        const isRecovered = isRecoveredAbandonment(subscription);
        const isAbandoned = isAbandonedAbandonment(subscription);

        if (isAbandoned) {
          return <SubscriptionStatusBadge status="abandoned" livemode={subscription.livemode} />;
        }
        if (isRecovered) {
          return (
            <Tooltip label={`Recovered on ${formatDateTime(subscription.recoveredAt)}`}>
              <StatusBadge status="recovered" label="Recovered" withInfoIcon />
            </Tooltip>
          );
        }
        return <Dash />;
      },
      enableSorting: false,
    });
  }

  if (!options.hideStatus) {
    columns.push({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const subscription = row.original;
        return (
          <SubscriptionStatusBadge
            status={subscription.status}
            livemode={subscription.livemode}
            error={subscription.paymentError}
          />
        );
      },
      enableSorting: false,
    });
  }

  return columns;
}

const PaymentsSubscriptions = ({
  title = 'Subscriptions',
  baseFilters = {},
  exportLabel = 'subscriptions',
  hidePageFilter = false,
  embedded = false,
  filterOptions = FILTER_OPTIONS,
}) => {
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, FILTER_OPTIONS);
  const page = query.page || undefined;

  const filter = {
    ...baseFilters,
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    livemode: true,
    page: page || undefined,
  };

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status: FILTER_OPTIONS[key].value === 'all' ? undefined : FILTER_OPTIONS[key].value,
    });

  const handlePageChange = value => setParams({ page: value || undefined });

  const leftFilterControls = !hidePageFilter ? (
    <div className="w-full md:w-64">
      <PageFilterInput
        name="page"
        pageTypes={['checkout']}
        productTypes={['subscription']}
        onChange={handlePageChange}
        initialValue={query.page}
        placeholder="Filter by checkout"
      />
    </div>
  ) : null;

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2 max-md:w-full [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1 lg:[&>*:first-child]:flex-none max-sm:[&>*:first-child]:basis-full">
      <SearchInput
        onChange={handleSearchChange}
        name="search_subscriptions"
        placeholder="Search by email or order ID"
        initialValue={search}
        className="lg:w-80"
      />
      <FilterInput options={filterOptions} onChange={handleStatusChange} value={status} />
      <ExportView
        collection="subscriptions"
        label={exportLabel}
        filters={{
          ...filter,
          sort: JSON.stringify(sort),
          search,
        }}
      />
    </div>
  );

  return (
    <div className={embedded ? 'space-y-4 px-4 pt-3 pb-4' : 'p-4 space-y-4'}>
      <PageHeader title={title} topBar={!embedded} />

      <PaymentsSubscriptionsList
        sort={sort}
        search={search}
        filter={filter}
        filterControls={filterControls}
        leftFilterControls={leftFilterControls}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};

export function PaymentsSubscriptionsList({
  sort,
  search,
  filter = {},
  options = {},
  refreshKey,
  infinite = true,
  emptyStateTitle,
  emptyStateSubtitle,
  filterControls,
  leftFilterControls,
  sorting: sortingProp,
  onSortingChange,
}) {
  const { sellerId } = useAuth();
  const { getInfiniteSubscriptions } = useSubscriptionQueries();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: ['subscriptions', sellerId, sort, search, filter, refreshKey],
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getInfiniteSubscriptions(sellerId, {
          ...filter,
          search,
          limit: 10,
          skip: pageParam * 10,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!infinite) return undefined;
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // react-table mounts each `cell` function as a component: a new columns array remounts every cell.
  const { hideCustomer, hidePaymentLink, hideAbandonment, hideStatus } = options;
  const columns = useMemo(
    () =>
      getSubscriptionColumns({
        options: { hideCustomer, hidePaymentLink, hideAbandonment, hideStatus },
      }),
    [hideCustomer, hidePaymentLink, hideAbandonment, hideStatus],
  );

  const isEmpty = !isLoading && flatData.length === 0;

  let emptyState;
  if (isEmpty && search) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No subscriptions found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && emptyStateTitle) {
    emptyState = (
      <EmptyState>
        {emptyStateTitle && <EmptyState.Title>{emptyStateTitle}</EmptyState.Title>}
        {emptyStateSubtitle && <EmptyState.Subtitle>{emptyStateSubtitle}</EmptyState.Subtitle>}
      </EmptyState>
    );
  } else if (isEmpty && filter.status) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No subscriptions yet</EmptyState.Title>
        <EmptyState.Subtitle>Your subscriptions will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && filter.livemode === false) {
    emptyState = (
      <EmptyState>
        <EmptyState.Subtitle>Test subscriptions will be shown here.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No subscriptions yet</EmptyState.Title>
        <EmptyState.Subtitle>Your subscriptions will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isError) {
    return (
      <div className="m-4">Unable to show subscriptions. Please refresh or contact support.</div>
    );
  }

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode={infinite ? 'infinite' : undefined}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sorting={sortingProp}
      onSortingChange={onSortingChange}
      toolbar={
        filterControls || leftFilterControls
          ? {
              leftActions: leftFilterControls,
              customActions: filterControls,
            }
          : undefined
      }
      getRowHref={subscription => `/subscriptions/${subscription._id}`}
      emptyState={emptyState}
      emptyMessage="No subscriptions found"
    />
  );
}

export default PaymentsSubscriptions;
